import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_IDENTITIES } from './productIdentity';
import { API_URL, buildKioskAuthHeaders, getStoredAuthToken } from './api';
import './KdsDisplaySettings.css';

const SCREENS = [
  ['incoming', 'Nouvelles commandes'], ['prep', 'En préparation'], ['ready', 'Prêtes'],
  ['assembly', 'Assemblage'], ['dispatch', 'Dispatch / Remise'],
];
const MODES = ['single', 'dual', 'full'];
const positiveId = value => Number.isSafeInteger(value) && value > 0;
function exactScreenMap(value, validValue) {
  return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === SCREENS.length
    && SCREENS.every(([key]) => Object.hasOwn(value, key) && validValue(value[key]));
}
function validSettings(settings) {
  return settings && MODES.includes(settings.screenMode)
    && exactScreenMap(settings.enabledScreens, value => typeof value === 'boolean')
    && exactScreenMap(settings.screenZoneMap, value => value === null || positiveId(value));
}
function parsePayload(payload) {
  if (payload?.schemaVersion !== 1 || !/^[a-f0-9]{64}$/.test(payload.revision || '') || !validSettings(payload.settings)
    || !Array.isArray(payload.zones) || !payload.zones.every(zone => zone && positiveId(zone.id) && typeof zone.name === 'string')
    || new Set(payload.zones.map(zone => zone.id)).size !== payload.zones.length) {
    throw new Error('Les paramètres reçus sont invalides. Rechargez-les avant de modifier.');
  }
  return { revision: payload.revision, settings: {
    screenMode: payload.settings.screenMode,
    enabledScreens: Object.fromEntries(SCREENS.map(([key]) => [key, payload.settings.enabledScreens[key]])),
    screenZoneMap: Object.fromEntries(SCREENS.map(([key]) => [key, payload.settings.screenZoneMap[key]])),
  }, zones: payload.zones.map(zone => ({ id: zone.id, name: zone.name })) };
}
const sameSettings = (left, right) => left.screenMode === right.screenMode && SCREENS.every(([key]) => left.enabledScreens[key] === right.enabledScreens[key] && left.screenZoneMap[key] === right.screenZoneMap[key]);
const validZone = (value, zones) => value === null || zones.some(zone => zone.id === value);

const SESSION_MESSAGE = 'Votre session a changé. Rechargez la page avant de modifier les paramètres.';

export default function KdsDisplaySettings({ allowed = false, product }) {
  const [open, setOpen] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const [sessionChanged, setSessionChanged] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const ownerToken = useRef(null), request = useRef(0), controller = useRef(null), heading = useRef(null), allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  const dirty = Boolean(draft && baseline && !sameSettings(draft, baseline.settings));
  const missingZones = draft && baseline ? SCREENS.filter(([key]) => !validZone(draft.screenZoneMap[key], baseline.zones)) : [];
  const noVisibleColumns = draft && !['incoming', 'prep', ...(draft.screenMode === 'dual' ? [] : ['ready'])].some(key => draft.enabledScreens[key]);

  useEffect(() => () => { request.current += 1; controller.current?.abort(); }, []);
  useEffect(() => { if (open) heading.current?.focus(); }, [open]);

  function checkSession() {
    if (!ownerToken.current || getStoredAuthToken() !== ownerToken.current) {
      setSessionChanged(true);
      throw new Error(SESSION_MESSAGE);
    }
  }

  async function transact(method, settings) {
    let sequence;
    let sent = false, definitiveFailure = false;
    try {
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      sequence = ++request.current;
      controller.current?.abort();
      controller.current = new AbortController();
      setBusy(true); setError(''); setMessage(''); setConfirmReload(false);
      const headers = buildKioskAuthHeaders();
      if (method === 'PUT') headers.set('Content-Type', 'application/json');
      sent = true;
      const response = await fetch(`${API_URL}/api/admin/kds-display-settings`, {
        method, headers, cache: 'no-store', signal: controller.current.signal,
        ...(method === 'PUT' ? { body: JSON.stringify({ revision: baseline.revision, settings }) } : {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (sequence !== request.current) return;
      checkSession();
      if (!allowedRef.current) throw new Error('Votre accès ne permet plus de modifier ces paramètres.');
      if (!response.ok) {
        definitiveFailure = response.status >= 400 && response.status < 500;
        if (response.status === 401 || response.status === 403) {
          setSessionChanged(true);
          throw new Error('Votre accès aux paramètres a été refusé. Votre saisie est conservée. Rechargez la page pour vérifier votre session.');
        }
        if (response.status === 409 && payload.code === 'KDS_DISPLAY_SETTINGS_CONFLICT') {
          setConflict(true);
          throw new Error('Les paramètres ont été modifiés ailleurs. Votre saisie est conservée. Rechargez les paramètres avant de les modifier à nouveau.');
        }
        throw new Error(typeof payload.error === 'string' ? payload.error : 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
      }
      const next = parsePayload(payload);
      setBaseline(next); setDraft(next.settings); setConflict(false);
      setMessage(method === 'PUT' ? 'Paramètres enregistrés.' : 'Paramètres actualisés.');
    } catch (failure) {
      if (sequence !== undefined && sequence !== request.current) return;
      if (method === 'PUT' && sent && !definitiveFailure) setConflict(true);
      setError(failure instanceof TypeError ? 'Réponse non reçue. Votre saisie est conservée. Rechargez les paramètres pour vérifier leur état avant un nouvel enregistrement.' : failure.message || 'Impossible de vérifier les paramètres. Votre saisie est conservée.');
    } finally {
      if (sequence === undefined || sequence === request.current) setBusy(false);
    }
  }

  function show() {
    setOpen(true);
    if (ownerToken.current === null) ownerToken.current = getStoredAuthToken();
    if (!baseline) transact('GET');
  }

  function save(event) {
    event.preventDefault();
    if (busy || conflict || sessionChanged || !allowed || !baseline) return;
    const settings = draft;
    if (!validSettings(settings) || missingZones.length) { setError('Remplacez chaque zone indisponible par une zone existante ou Toutes zones avant d’enregistrer.'); return; }
    transact('PUT', settings);
  }

  function edit(next) { setDraft(next); setMessage(''); setConfirmReload(false); }
  const openingLinks = baseline && allowed && !sessionChanged && !conflict && !busy && Object.hasOwn(PRODUCT_IDENTITIES, product)
    ? SCREENS.filter(([key]) => baseline.settings.enabledScreens[key] && validZone(baseline.settings.screenZoneMap[key], baseline.zones)) : [];

  if (!open) return allowed ? <button type="button" className="kds-display-settings-button is-secondary kds-display-settings-open" onClick={show}>Affichage KDS</button> : null;

  return <section className="kds-display-settings" aria-labelledby="kds-display-settings-title">
    <div className="kds-display-settings-head"><h3 id="kds-display-settings-title" ref={heading} tabIndex={-1}>Affichage KDS</h3><button type="button" className="kds-display-settings-button is-secondary" disabled={busy} onClick={() => setOpen(false)}>Fermer l’affichage</button></div>
    <p>Ces réglages organisent les vues cuisine. Ils ne configurent pas un appareil et n’ajoutent aucun poste. La zone imposée à un poste sous licence reste prioritaire.</p>
    {!allowed && <p role="alert">Votre accès ne permet plus de modifier ces paramètres. Votre saisie est conservée.</p>}
    {error && <p className="kds-display-settings-error" role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {busy && <p role="status">Vérification des paramètres…</p>}
    {draft && <form onSubmit={save}>
      <fieldset disabled={busy || !allowed || sessionChanged}>
        <legend className="product-options-sr-only">Réglages de l’affichage KDS</legend>
        <label className="kds-display-mode"><span>Mode d’affichage</span><select aria-label="Mode d’affichage" value={draft.screenMode} onChange={event => edit({ ...draft, screenMode: event.target.value })}>
          <option value="single">Simple</option><option value="dual">Double</option><option value="full">Complet</option>
        </select></label>
        <p>Simple et Complet affichent actuellement les mêmes trois colonnes possibles : Nouvelles commandes, En préparation et Prêtes. Double masque Prêtes. Seules les étapes activées apparaissent. Assemblage et Dispatch / Remise utilisent des vues dédiées.</p>
        {noVisibleColumns && <p className="kds-display-settings-notice" role="status">Aucune colonne ne sera visible dans la vue globale avec ce mode et ces étapes. Les vues dédiées activées restent accessibles.</p>}
        {missingZones.length > 0 && <p role="alert" className="kds-display-settings-error">Une zone enregistrée est indisponible. Corrigez son choix, même si l’étape est désactivée, avant d’enregistrer.</p>}
        <div className="kds-display-stages">{SCREENS.map(([key, label]) => <div className="kds-display-stage" key={key}>
          <label className="kds-display-toggle"><input type="checkbox" checked={draft.enabledScreens[key]} onChange={event => edit({ ...draft, enabledScreens: { ...draft.enabledScreens, [key]: event.target.checked } })} /><span>Activer {label}</span></label>
          <label className="kds-display-zone"><span>Zone · {label}</span><select aria-label={`Zone · ${label}`} value={draft.screenZoneMap[key] ?? ''} onChange={event => edit({ ...draft, screenZoneMap: { ...draft.screenZoneMap, [key]: event.target.value === '' ? null : Number(event.target.value) } })}>
            <option value="">Toutes zones</option>
            {!validZone(draft.screenZoneMap[key], baseline.zones) && <option value={draft.screenZoneMap[key]} disabled>Zone indisponible (n°{draft.screenZoneMap[key]})</option>}
            {baseline.zones.map(zone => <option key={zone.id} value={zone.id}>{zone.name} (n°{zone.id})</option>)}
          </select></label>
        </div>)}</div>
        <div className="kds-display-settings-actions"><button type="submit" className="kds-display-settings-button" disabled={!dirty || conflict || missingZones.length > 0}>Enregistrer l’affichage</button><button type="button" className="kds-display-settings-button is-secondary" disabled={!dirty} onClick={() => { setDraft(baseline.settings); setMessage('Modifications locales annulées.'); setConfirmReload(false); }}>Annuler les modifications</button></div>
      </fieldset>
    </form>}
    {baseline && <div className="kds-display-views"><h4>Vues de la configuration enregistrée</h4><p>Les liens utilisent les étapes et zones enregistrées, sans appliquer votre brouillon. Les droits du poste et ses limites restent contrôlés à l’ouverture.</p>
      {openingLinks.length ? <div className="kds-display-settings-actions">{openingLinks.map(([key, label]) => <Link key={key} to={`/cuisine?screen=${key}&product=${product}&from=options`} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Ouvrir {label}<span className="product-options-sr-only"> (nouvel onglet)</span></Link>)}</div> : <p>Aucune vue confirmée à ouvrir. Vérifiez la configuration enregistrée et les zones disponibles.</p>}
    </div>}
    <div className="kds-display-settings-actions"><button type="button" className="kds-display-settings-button is-secondary" disabled={busy || !allowed || sessionChanged} onClick={() => dirty ? setConfirmReload(true) : transact('GET')}>Recharger l’affichage</button></div>
    {confirmReload && <div className="kds-display-settings-confirm" role="group" aria-label="Confirmer le rechargement"><p>Recharger remplacera votre saisie par les paramètres enregistrés sur le serveur.</p><button type="button" className="kds-display-settings-button is-secondary" onClick={() => transact('GET')}>Recharger et remplacer ma saisie</button><button type="button" className="kds-display-settings-button is-secondary" onClick={() => setConfirmReload(false)}>Conserver ma saisie</button></div>}
  </section>;
}
